/**
 * SupplySelector — Horizontal card-based variant picker
 *
 * Lives INSIDE <variant-radios> so that checking a radio input
 * triggers the existing VariantRadios.onVariantChange() flow.
 * This element only handles:
 *   - Card click → visual selection + radio check
 *   - Dynamic price + per-serving updates when purchase type changes
 *
 * All variant state management (URL, hidden inputs, globalVariables,
 * recharge widget, image gallery) is handled by VariantRadios/VariantSelects.
 */
class SupplySelector extends HTMLElement {
  constructor() {
    super();
    this.cards = [];
    this.form = null;
  }

  connectedCallback() {
    this.form = this.closest('form');
    this.cards = [...this.querySelectorAll('[data-supply-card]')].filter(Boolean);

    this._bindCardClicks();
    this._observePurchaseTypeChanges();

    // Initial price update (in case subscription is pre-selected)
    requestAnimationFrame(() => this._updateAllCardPrices());

    // Initial welcome kit state from selected supply card's variant
    const selectedCard = this.cards.find((c) => c.classList.contains('supply-card--selected')) || this.cards[0];
    if (selectedCard?.dataset.variantId) {
      requestAnimationFrame(() => this._updateWelcomeKitForVariant(selectedCard.dataset.variantId));
    }
  }

  /* ------------------------------------------------
   * Card selection
   * ---------------------------------------------- */

  _bindCardClicks() {
    this.cards.forEach((card) => {
      card.addEventListener('click', (e) => {
        // Don't double-fire if clicking the label/radio directly
        if (e.target.closest('label') && e.target.tagName !== 'INPUT') {
          e.preventDefault();
        }
        this._selectCard(card);
      });
    });
  }

  /**
   * Select a card: update visuals, check radio, let variant-radios handle the rest.
   * Explicitly update welcome kit (bonus_kit) from variant metafield so it shows/hides correctly.
   */
  _selectCard(card) {
    if (!card || !card.querySelector) return;
    // Update visual state
    this.cards.forEach((c) => {
      const isSelected = c === card;
      c.classList.toggle('supply-card--selected', isSelected);
      c.setAttribute('aria-checked', String(isSelected));
      c.tabIndex = isSelected ? 0 : -1;
    });

    // Check the radio input — variant-radios picks up the change event
    const radio = card.querySelector('input[type="radio"]');
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Update welcome kit from selected variant's bonus_kit metafield (same as vertical version)
    const variantId = card.dataset.variantId;
    if (variantId) {
      requestAnimationFrame(() => this._updateWelcomeKitForVariant(variantId));
    }
  }

  /**
   * Show/hide welcome kit (data-bonus-kit-wrapper) based on variant's bonus_kit metafield.
   * Mirrors product-form-variant-selects.js _updateBonusKit logic.
   */
  _updateWelcomeKitForVariant(variantId) {
    const wrapper = this.form?.querySelector('[data-bonus-kit-wrapper]');
    if (!wrapper) return;

    const metafieldsScript = this.form?.querySelector('[data-variantJSON-metafields]');
    const variantsScript = this.form?.querySelector('[data-variantJSON]');
    if (!metafieldsScript || !variantsScript) return;

    let metafieldsByVariant = null;
    let variantsData = null;
    try {
      metafieldsByVariant = JSON.parse(metafieldsScript.textContent);
      variantsData = JSON.parse(variantsScript.textContent);
    } catch (e) {
      return;
    }

    const variant = Array.isArray(variantsData) ? variantsData.find((v) => String(v.id) === String(variantId)) : null;
    if (!variant) return;

    const meta = metafieldsByVariant[variantId] || metafieldsByVariant[Number(variantId)];
    const bonusKitData = meta?.bonus_kit || null;
    const hasBonusKit = bonusKitData && typeof bonusKitData === 'object';

    const imageEl = wrapper.querySelector('[data-bonus-kit-image]');
    const titleEl = wrapper.querySelector('[data-bonus-kit-title]');
    const linkEl = wrapper.querySelector('[data-bonus-kit-link]');

    if (!hasBonusKit || !bonusKitData.title) {
      wrapper.classList.add('d-none');
      wrapper.setAttribute('aria-hidden', 'true');
      if (imageEl) {
        imageEl.removeAttribute('src');
        imageEl.alt = '';
      }
      if (titleEl) titleEl.textContent = '';
      if (linkEl) {
        linkEl.setAttribute('href', '#');
        linkEl.classList.add('d-none');
      }
      return;
    }

    wrapper.classList.remove('d-none');
    wrapper.removeAttribute('aria-hidden');
    if (titleEl) titleEl.textContent = bonusKitData.title || '';
    if (imageEl) {
      if (bonusKitData.product_image) {
        imageEl.src = bonusKitData.product_image;
      } else {
        imageEl.removeAttribute('src');
      }
      imageEl.alt = bonusKitData.product_alt || bonusKitData.title || '';
    }
    if (linkEl) {
      if (bonusKitData.product_url) {
        linkEl.href = bonusKitData.product_url;
        linkEl.classList.remove('d-none');
      } else {
        linkEl.setAttribute('href', '#');
        linkEl.classList.add('d-none');
      }
    }
  }

  /* ------------------------------------------------
   * Dynamic price updates (subscription ↔ one-time)
   * ---------------------------------------------- */

  _observePurchaseTypeChanges() {
    if (!this.form) return;

    const rechargeWidget = this.form.querySelector('recharge-widget');
    if (!rechargeWidget) return;

    // Listen for clicks on purchase type containers — only update when user clicked (not when variant change triggers programmatic update)
    rechargeWidget.querySelectorAll('[data-purchase_type]').forEach((container) => {
      container.addEventListener('click', (e) => {
        if (!e.isTrusted) return;
        setTimeout(() => this._updateAllCardPrices(), 60);
      });
    });

    // Listen for selling plan frequency changes — only when user changed it, not programmatic
    rechargeWidget.querySelectorAll('custom-select').forEach((select) => {
      select.addEventListener('change', (e) => {
        if (!e.isTrusted) return;
        setTimeout(() => this._updateAllCardPrices(), 60);
      });
    });
  }

  /**
   * Update item price on every supply card.
   * Shows the full variant price (subscription or one-time) with a crossed-out compare price when discounted.
   */
  _updateAllCardPrices() {
    const purchaseType = this._getCurrentPurchaseType();
    const discountPercent = this._getCurrentDiscountPercent();
    const moneyFormat = window.globalVariables?.money_format || '${{amount}}';

    // Use fresh card refs in case DOM was updated
    const cards = [...this.querySelectorAll('[data-supply-card]')].filter(Boolean);
    cards.forEach((card) => {
      if (!card) return;
      const priceWrapper = card.querySelector('[data-supply-per-serving]');
      if (!priceWrapper) return;

      const onetimePrice = parseInt(priceWrapper.dataset.supplyOnetimePrice, 10) || 0;
      const subscriptionPrice = parseInt(priceWrapper.dataset.supplySubscriptionPrice, 10) || 0;
      const comparePrice = parseInt(priceWrapper.dataset.supplyComparePrice, 10) || 0;
      const subComparePrice = parseInt(priceWrapper.dataset.supplySubComparePrice, 10) || 0;

      const itemPriceEl = priceWrapper.querySelector('[data-supply-item-price]');
      const compareEl = priceWrapper.querySelector('[data-supply-compare-el]');
      if (!itemPriceEl || !compareEl) return;

      let displayPrice = onetimePrice;
      let comparePriceToShow = 0;

      if (purchaseType === 'subscription') {
        if (subscriptionPrice > 0) {
          displayPrice = subscriptionPrice;
        } else if (discountPercent > 0) {
          displayPrice = Math.round(onetimePrice * (1 - discountPercent / 100));
        }
        // For subscription: cross out the one-time price (or compare_at_price if higher)
        const subCompare = subComparePrice > 0 ? subComparePrice : comparePrice;
        if (subCompare > displayPrice) {
          comparePriceToShow = subCompare;
        } else if (onetimePrice > displayPrice) {
          comparePriceToShow = onetimePrice;
        }
      } else {
        // For one-time: cross out compare_at_price if it exists and is higher
        if (comparePrice > onetimePrice) {
          comparePriceToShow = comparePrice;
        }
      }

      const hasDiscount = comparePriceToShow > displayPrice;

      itemPriceEl.textContent = Utility.formatMoney(displayPrice, moneyFormat);
      itemPriceEl.classList.toggle('text-danger', hasDiscount);

      if (hasDiscount) {
        compareEl.textContent = Utility.formatMoney(comparePriceToShow, moneyFormat);
        compareEl.setAttribute('aria-label', 'Previous price was ' + Utility.formatMoney(comparePriceToShow, moneyFormat));
        compareEl.removeAttribute('aria-hidden');
        compareEl.style.display = '';
      } else {
        compareEl.textContent = '';
        compareEl.setAttribute('aria-hidden', 'true');
        compareEl.style.display = 'none';
      }

      priceWrapper.classList.add('supply-card__per-serving--loaded');
    });
  }

  /* ------------------------------------------------
   * Helpers
   * ---------------------------------------------- */

  _getCurrentPurchaseType() {
    if (!this.form) return 'onetime';
    const checked = this.form.querySelector('[data-purchase_type] input[name="selling_plan"]:checked');
    if (!checked) return 'onetime';
    const container = checked.closest('[data-purchase_type]');
    return container?.getAttribute('data-purchase_type') || 'onetime';
  }

  _getCurrentDiscountPercent() {
    if (!this.form) return 0;
    const checked = this.form.querySelector('[data-purchase_type="subscription"] input[name="selling_plan"]:checked');
    if (!checked) return 0;

    // Try to get discount from the selected selling plan option
    const selectedPlanRadio = this.form.querySelector('input[name="selling_plan_group"]:checked')
      || this.form.querySelector('[data-purchase_type="subscription"] input[name="selling_plan"]:checked');
    return parseInt(selectedPlanRadio?.dataset?.discount, 10) || 0;
  }
}

customElements.define('supply-selector', SupplySelector);
